import React, { useState, useEffect, useMemo } from 'react';
import { DocumentTextIcon, FunnelIcon, ArrowPathIcon, CurrencyDollarIcon, ClockIcon, MagnifyingGlassIcon, CheckCircleIcon, DocumentMinusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useXmlTerceiros, useXmlTerceirosStats } from '../hooks/useXmlTerceiros';
import type { XmlTerceirosFilters, XmlTerceiro } from '../types/xmlTerceiros';
import XmlTerceirosList from '../components/xmlTerceiros/XmlTerceirosList';
import XmlTerceirosModal from '../components/xmlTerceiros/XmlTerceirosModal';
import { money } from '../utils/format';
import { btnPrimario, btnSecundario, inputIris } from '../components/ui/iris';

const XmlTerceirosPage: React.FC = () => {
  const [filters, setFilters] = useState<XmlTerceirosFilters>({ dias: 30 });
  const [selectedXml, setSelectedXml] = useState<XmlTerceiro | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Debounce da busca para não disparar queries a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const activeFilters = useMemo<XmlTerceirosFilters>(
    () => ({ ...filters, search: searchTerm || undefined }),
    [filters, searchTerm]
  );

  const { data: response, isLoading, error, refetch } = useXmlTerceiros(
    activeFilters,
    currentPage,
    itemsPerPage
  );
  const { data: stats } = useXmlTerceirosStats(activeFilters);

  const xmlTerceiros = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const totalItems = response?.pagination?.totalItems || 0;
  const startIndex =
    ((response?.pagination?.page || 1) - 1) *
    (response?.pagination?.pageSize || itemsPerPage);

  // Reset de página quando filtros/busca mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, itemsPerPage]);

  const handleRefresh = () => refetch();

  const handleOpenModal = (xml: XmlTerceiro) => {
    setSelectedXml(xml);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedXml(null);
    setIsModalOpen(false);
  };

  const handleClearAll = () => {
    setSearchInput('');
    setSearchTerm('');
    setFilters({ dias: 30 });
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-8">
          <DocumentMinusIcon className="h-16 w-16 text-danger mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-tx mb-2">
            Erro ao carregar NF-e de Terceiros
          </h1>
          <p className="text-tx-2 mb-4">
            Não foi possível carregar os dados. Tente novamente.
          </p>
          <button onClick={handleRefresh} className={btnPrimario}>
            <ArrowPathIcon className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <DocumentTextIcon className="h-7 w-7 text-accent-text flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">NF-e de Terceiros</h1>
            <p className="text-xs text-tx-2">
              Gerenciar notas fiscais eletrônicas recebidas
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 bg-surface-2 hover:bg-surface rounded-iris transition-colors disabled:opacity-50"
            title="Atualizar lista"
          >
            <ArrowPathIcon
              className={`h-5 w-5 text-tx-2 ${isLoading ? 'animate-spin' : ''}`}
            />
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-iris transition-colors ${
              showFilters
                ? 'bg-accent-soft text-accent-text'
                : 'bg-surface-2 hover:bg-surface text-tx-2'
            }`}
            title="Filtros avançados"
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">
                  Total de NF-e
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-tx">
                  {stats?.total || 0}
                </p>
              </div>
              <div className="bg-accent-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <DocumentTextIcon className="h-4 w-4 lg:h-6 lg:w-6 text-accent-text" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">
                  Pendentes
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-warning">
                  {stats?.pendentes || 0}
                </p>
              </div>
              <div className="bg-warning-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <ClockIcon className="h-4 w-4 lg:h-6 lg:w-6 text-warning" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">
                  Processados
                </p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-ok">
                  {stats?.processados || 0}
                </p>
              </div>
              <div className="bg-ok-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <CheckCircleIcon className="h-4 w-4 lg:h-6 lg:w-6 text-ok" />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-iris-lg p-4 lg:p-6 shadow-sm border border-line">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm font-medium text-tx-2 mb-1">
                  Valor Total
                </p>
                <p className="text-base sm:text-lg lg:text-2xl font-bold text-ok truncate">
                  {money(stats?.valorTotal)}
                </p>
              </div>
              <div className="bg-ok-soft p-2 lg:p-3 rounded-full flex-shrink-0">
                <CurrencyDollarIcon className="h-4 w-4 lg:h-6 lg:w-6 text-ok" />
              </div>
            </div>
          </div>
        </div>

        {/* Busca e Filtros */}
        <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-4 lg:p-6">
          <div className="space-y-4">
            {/* Busca Principal */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-tx-3" />
                  <input
                    type="text"
                    placeholder="Buscar por emitente, CNPJ ou número da NF-e..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className={`w-full pl-10 ${inputIris}`}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                {/* Filtros de Período */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Hoje', value: 1 },
                    { label: '7 dias', value: 7 },
                    { label: '15 dias', value: 15 },
                    { label: '30 dias', value: 30 },
                    { label: '60 dias', value: 60 },
                    { label: '90 dias', value: 90 },
                    { label: 'Personalizado', value: null },
                  ].map((periodo) => {
                    const active =
                      periodo.value === null
                        ? filters.dias === null || !!filters.dataInicio || !!filters.dataFim
                        : filters.dias === periodo.value &&
                          !filters.dataInicio &&
                          !filters.dataFim;
                    return (
                      <button
                        key={periodo.label}
                        onClick={() => {
                          if (periodo.value === null) {
                            const hoje = new Date();
                            const dataFimDefault = hoje.toISOString().split('T')[0];
                            const trintaDiasAtras = new Date();
                            trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30);
                            const dataInicioDefault = trintaDiasAtras
                              .toISOString()
                              .split('T')[0];
                            setFilters((prev) => ({
                              ...prev,
                              dias: null,
                              dataInicio: prev.dataInicio || dataInicioDefault,
                              dataFim: prev.dataFim || dataFimDefault,
                            }));
                          } else {
                            setFilters((prev) => ({
                              ...prev,
                              dias: periodo.value,
                              dataInicio: undefined,
                              dataFim: undefined,
                            }));
                          }
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-2 text-sm font-medium rounded-iris transition-colors ${
                          active
                            ? 'bg-accent text-white'
                            : 'bg-surface-2 text-tx-2 hover:bg-surface'
                        }`}
                      >
                        {periodo.label}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleClearAll}
                  className="px-3 py-2 lg:py-3 text-sm font-medium text-tx-2 bg-surface-2 rounded-iris hover:bg-surface transition-colors"
                >
                  Limpar
                </button>
              </div>
            </div>

            {/* Intervalo de Datas Personalizado */}
            {(filters.dias === null || filters.dataInicio || filters.dataFim) && (
              <div className="flex flex-wrap items-center gap-3 mt-4 p-3 bg-surface-2 rounded-iris">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-tx-2">De:</label>
                  <input
                    type="date"
                    value={filters.dataInicio || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        dataInicio: e.target.value || undefined,
                        dias: null,
                      }));
                      setCurrentPage(1);
                    }}
                    className={inputIris}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-tx-2">Até:</label>
                  <input
                    type="date"
                    value={filters.dataFim || ''}
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        dataFim: e.target.value || undefined,
                        dias: null,
                      }));
                      setCurrentPage(1);
                    }}
                    className={inputIris}
                  />
                </div>
                {(filters.dataInicio || filters.dataFim) && (
                  <button
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        dataInicio: undefined,
                        dataFim: undefined,
                        dias: 30,
                      }));
                      setCurrentPage(1);
                    }}
                    className="text-sm text-tx-3 hover:text-tx underline"
                  >
                    Limpar datas
                  </button>
                )}
              </div>
            )}

            {/* Filtros Avançados */}
            {showFilters && (
              <div className="border-t border-line pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Emitente
                    </label>
                    <input
                      type="text"
                      placeholder="Nome do emitente"
                      value={filters.emitenteNome || ''}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          emitenteNome: e.target.value || undefined,
                        }))
                      }
                      className={`w-full ${inputIris}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      CNPJ
                    </label>
                    <input
                      type="text"
                      placeholder="CNPJ do emitente"
                      value={filters.cnpj || ''}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          cnpj: e.target.value || undefined,
                        }))
                      }
                      className={`w-full ${inputIris}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Número NF-e
                    </label>
                    <input
                      type="text"
                      placeholder="Número da NF-e"
                      value={filters.numeroNfe || ''}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          numeroNfe: e.target.value || undefined,
                        }))
                      }
                      className={`w-full ${inputIris}`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-tx-2 mb-2">
                      Status
                    </label>
                    <select
                      value={
                        filters.pendente === undefined
                          ? ''
                          : filters.pendente
                          ? 'true'
                          : 'false'
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters((prev) => ({
                          ...prev,
                          pendente:
                            value === '' ? undefined : value === 'true',
                        }));
                      }}
                      className={`w-full ${inputIris}`}
                    >
                      <option value="">Todos</option>
                      <option value="true">Pendente</option>
                      <option value="false">Processado</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        emitenteNome: undefined,
                        cnpj: undefined,
                        numeroNfe: undefined,
                        pendente: undefined,
                      }))
                    }
                    className={btnSecundario}
                  >
                    Limpar avançados
                  </button>
                  <button
                    onClick={() => setShowFilters(false)}
                    className={btnPrimario}
                  >
                    Fechar Filtros
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lista de NF-e */}
        <div>
          <XmlTerceirosList
            xmlTerceiros={xmlTerceiros}
            onSelect={handleOpenModal}
            isLoading={isLoading}
          />
        </div>

        {/* Paginação e Controles */}
        <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-4 lg:p-6">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-sm text-tx-2 order-2 sm:order-1">
                Mostrando{' '}
                <span className="font-medium">
                  {totalItems === 0 ? 0 : startIndex + 1}
                </span>{' '}
                a{' '}
                <span className="font-medium">
                  {Math.min(startIndex + itemsPerPage, totalItems)}
                </span>{' '}
                de <span className="font-medium">{totalItems}</span> registros
              </div>

              <div className="flex flex-wrap items-center gap-2 order-1 sm:order-2">
                <label className="text-sm text-tx-2 whitespace-nowrap">
                  Mostrar:
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className={inputIris}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span className="text-sm text-tx-2 whitespace-nowrap">
                  por página
                </span>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="hidden sm:inline-flex px-3 py-2 text-sm border border-line rounded-iris hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Primeira
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-line rounded-iris hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Página anterior"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <div className="px-3 py-2 bg-accent-soft text-accent-text rounded-iris text-sm font-medium min-w-0">
                    <span className="whitespace-nowrap">
                      {currentPage} / {totalPages}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 border border-line rounded-iris hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Próxima página"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="hidden sm:inline-flex px-3 py-2 text-sm border border-line rounded-iris hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Última
                  </button>
                </div>

                <div className="sm:hidden flex items-center gap-2">
                  <span className="text-xs text-tx-3">Ir para:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = Number(e.target.value);
                      if (page >= 1 && page <= totalPages) setCurrentPage(page);
                    }}
                    className={`w-16 text-center ${inputIris}`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedXml && (
        <XmlTerceirosModal
          xml={selectedXml}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default XmlTerceirosPage;
